import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  BarChart3, Globe, TrendingDown, AlertTriangle, Folder,
  ArrowRight, DollarSign,
} from "lucide-react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";

const PALETTE = ["#D4A017", "#1A3C5E", "#1B4332", "#8B4513", "#C0392B", "#27AE60", "#6B7280"];

const SCORE_COLOR = (s) =>
  s >= 70 ? "#27AE60" : s >= 50 ? "#D4A017" : s >= 30 ? "#E67E22" : "#C0392B";

export default function Visualizations() {
  const [data, setData] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDash, setProjectDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectLoading, setProjectLoading] = useState(false);

  useEffect(() => {
    api
      .get("/visualizations/summary")
      .then((r) => {
        setData(r.data);
        const firstAnalyzed = (r.data?.projects || []).find((p) => p.has_analyses);
        if (firstAnalyzed) setSelectedProject(firstAnalyzed.id);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setProjectDash(null);
      return;
    }
    setProjectLoading(true);
    api
      .get(`/projects/${selectedProject}/dashboard`)
      .then((r) => setProjectDash(r.data))
      .catch(() => setProjectDash(null))
      .finally(() => setProjectLoading(false));
  }, [selectedProject]);

  const radarData = useMemo(() => {
    if (!data) return [];
    const a = data.averages || {};
    return [
      { axis: "Juridique", value: a.juridique || 0 },
      { axis: "Env. (SEC)", value: a.sec || 0 },
      { axis: "Social (SSC)", value: a.ssc || 0 },
      { axis: "Souv. (SOS)", value: a.sos || 0 },
      { axis: "Global", value: a.global || 0 },
    ];
  }, [data]);

  const violationsPie = useMemo(() => {
    if (!data) return [];
    const v = data.totals?.violations || {};
    return [
      { name: "Critique", value: v.critique || 0 },
      { name: "Grave", value: v.grave || 0 },
      { name: "Modérée", value: v.moderee || 0 },
      { name: "Mineure", value: v.mineure || 0 },
    ].filter((x) => x.value > 0);
  }, [data]);

  if (loading || !data) {
    return (
      <div className="p-6 text-sm opacity-60" data-testid="visualizations-page-loading">
        Chargement des visualisations…
      </div>
    );
  }

  const t = data.totals || {};
  const analyzedProjects = (data.projects || []).filter((p) => p.has_analyses);

  return (
    <div className="p-6 space-y-6" data-testid="visualizations-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <BarChart3 size={28} style={{ color: "#D4A017" }} />
          Visualisations
        </h1>
        <p className="text-sm opacity-70 mt-1">
          Vue agrégée sur l'ensemble de vos conventions + accès direct aux graphiques par projet.
        </p>
      </div>

      {/* Aggregated KPIs */}
      <div className="grid md:grid-cols-5 gap-3">
        <Stat label="Projets" value={t.projects} icon={Folder} color="#1A3C5E" testid="viz-kpi-projects" />
        <Stat label="Analysés" value={t.analyzed} icon={BarChart3} color="#27AE60" testid="viz-kpi-analyzed" />
        <Stat
          label="Score global moyen"
          value={`${data.averages?.global || 0}/100`}
          icon={TrendingDown}
          color={SCORE_COLOR(data.averages?.global || 0)}
          testid="viz-kpi-score"
        />
        <Stat
          label="Violations critiques"
          value={t.violations?.critique || 0}
          icon={AlertTriangle}
          color="#C0392B"
          testid="viz-kpi-critique"
        />
        <Stat
          label="Manque à gagner (Mrd USD)"
          value={`${((t.manque_a_gagner_musd || 0) / 1000).toFixed(2)}`}
          icon={DollarSign}
          color="#D4A017"
          testid="viz-kpi-manque"
        />
      </div>

      {/* Aggregated charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rap-card p-5" data-testid="viz-radar-averages">
          <h2 className="font-display font-bold text-base mb-3">
            Profil moyen — tous projets
          </h2>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Radar
                  dataKey="value"
                  stroke="#D4A017"
                  fill="#D4A017"
                  fillOpacity={0.35}
                />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rap-card p-5" data-testid="viz-pie-violations">
          <h2 className="font-display font-bold text-base mb-3">
            Répartition des violations (réseau)
          </h2>
          {violationsPie.length > 0 ? (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={violationsPie}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {violationsPie.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-xs opacity-60 text-center py-12">
              Aucune fiche diagnostic générée pour le moment.
            </div>
          )}
        </div>

        <div className="rap-card p-5" data-testid="viz-bar-country">
          <h2 className="font-display font-bold text-base mb-3">
            Projets par pays
          </h2>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={data.distribution?.by_country || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#1A3C5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rap-card p-5" data-testid="viz-top-risk">
          <h2 className="font-display font-bold text-base mb-3">
            Top 5 pays à risque (score global le plus bas)
          </h2>
          {(data.top_risk_countries || []).length > 0 ? (
            <div className="space-y-2">
              {data.top_risk_countries.map((c, i) => (
                <div
                  key={c.country}
                  className="flex items-center gap-3 p-2 rounded-sm border"
                  style={{ borderColor: "hsl(var(--border))" }}
                  data-testid={`viz-risk-${i}`}
                >
                  <span className="w-6 text-center font-mono opacity-70">#{i + 1}</span>
                  <Globe size={14} style={{ color: "#C0392B" }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{c.country}</div>
                    <div className="text-[10px] opacity-60 font-mono">
                      {c.projects} projet(s)
                    </div>
                  </div>
                  <span
                    className="text-xs font-mono font-bold px-2 py-0.5 rounded-sm"
                    style={{
                      color: SCORE_COLOR(c.avg_score),
                      background: `${SCORE_COLOR(c.avg_score)}15`,
                    }}
                  >
                    {c.avg_score}/100
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs opacity-60 text-center py-12">
              Lancez les analyses sur vos projets pour afficher ce classement.
            </div>
          )}
        </div>
      </div>

      {/* Per-project explorer */}
      <div className="rap-card p-5" data-testid="viz-project-explorer">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-base">
            Explorer un projet
          </h2>
          <select
            value={selectedProject || ""}
            onChange={(e) => setSelectedProject(e.target.value || null)}
            className="text-xs border rounded-sm bg-transparent px-2 py-1"
            style={{ borderColor: "hsl(var(--border))" }}
            data-testid="viz-project-select"
          >
            <option value="">— choisir un projet —</option>
            {(data.projects || []).map((p) => (
              <option key={p.id} value={p.id} disabled={!p.has_analyses}>
                {p.name} {p.has_analyses ? `(${p.scores.global}/100)` : "(sans analyses)"}
              </option>
            ))}
          </select>
        </div>

        {!selectedProject && (
          <div className="text-xs opacity-60 text-center py-12">
            Sélectionnez un projet pour afficher son tableau de bord complet (12 KPIs + 8 graphiques).
          </div>
        )}

        {selectedProject && projectLoading && (
          <div className="text-xs opacity-60 py-6">Chargement…</div>
        )}

        {selectedProject && !projectLoading && projectDash && (
          <div className="space-y-3">
            <div className="grid md:grid-cols-4 gap-3">
              <MiniScore label="Juridique" value={projectDash.analyses?.juridique?.score} testid="viz-proj-jur" />
              <MiniScore label="Env. (SEC)" value={projectDash.analyses?.environnemental?.score} testid="viz-proj-sec" />
              <MiniScore label="Social (SSC)" value={projectDash.analyses?.social?.score} testid="viz-proj-ssc" />
              <MiniScore label="Souv. (SOS)" value={projectDash.analyses?.souverainete?.score} testid="viz-proj-sos" />
            </div>
            <Link
              to={`/projects/${selectedProject}`}
              className="inline-flex items-center gap-2 text-sm font-semibold"
              style={{ color: "#D4A017" }}
              data-testid="viz-proj-open"
            >
              Ouvrir le tableau de bord complet <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rap-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div className="font-mono text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function MiniScore({ label, value, testid }) {
  const v = Number(value || 0);
  return (
    <div
      className="p-3 rounded-sm border"
      style={{ borderColor: "hsl(var(--border))" }}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70 mb-1">{label}</div>
      <div className="font-mono text-xl font-bold" style={{ color: SCORE_COLOR(v) }}>
        {v}<span className="text-[10px] opacity-60">/100</span>
      </div>
    </div>
  );
}
